import { useEffect, useState, useMemo } from 'react';
import { DealUIObjectWithBrand } from './DealsSection';
import { DealsSortBy, filterState } from './state';
import { getAllDeals } from 'common/api/deals/getDealsByLinkId';
import { getBrandsFromDealIds } from 'common/api/constructor/getBrandsFromDealIds';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';
import { defaultConfigKeys } from 'common/modules/remote-config/defaultConfigKeys';
import { firebaseValueAsNumber, useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { DealUIObject } from 'common/api/deals/types';

const uniqId = (id: string, index: number, array: string[]) => array.indexOf(id) === index;

const dealTypeMap = {
    'Online Deal': 'deal',
    'Online Coupon': 'coupon',
} as const;

const isDealTypeLabel = (value: string): value is keyof typeof dealTypeMap => value in dealTypeMap;

type DealsFetcher = (maxPerBrandCount: number, maxResults: number) => Promise<DealUIObject[]>;

const fetchAllDeals: DealsFetcher = (maxPerBrandCount) => getAllDeals(maxPerBrandCount);

export function useDealsForFilterState(
    state: filterState,
    maxResults: number,
    fetchDeals: DealsFetcher = fetchAllDeals
) {
    const [allDeals, setAllDeals] = useState<DealUIObjectWithBrand[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDeals, setCurrentDeals] = useState<DealUIObjectWithBrand[]>([]);
    const defaultFilters = useConstructorDefaultParameters({});

    const brandLimitConfig = useFirebaseConfigValues(
        'top_deals_brand_limit',
        defaultConfigKeys.top_deals_brand_limit,
        firebaseValueAsNumber
    );

    // Brand limiting is already applied while the deal pages are fetched.
    // This memo only enforces the maxResults cap.
    const allDealsBrandFiltered = useMemo(
        () => allDeals.slice(0, maxResults),
        [allDeals, maxResults]
    );

    // Fetch only enough ranked deals to populate the visible list.
    useEffect(() => {
        setLoading(true);
        setAllDeals([]);

        if (!defaultFilters.current || !brandLimitConfig) {
            return;
        }

        fetchDeals(brandLimitConfig ?? 0, maxResults).then(async (deals) => {
            const ids = deals.map((deal) => deal.affiliateId).filter(uniqId);

            // Batch brand lookups in chunks of 200 to stay within Constructor API limits
            const BATCH_SIZE = 200;
            const brandBatches = await Promise.all(
                Array.from({ length: Math.ceil(ids.length / BATCH_SIZE) }, (_, i) =>
                    getBrandsFromDealIds(ids.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE), {
                        ...defaultFilters.current,
                        resultsPerPage: BATCH_SIZE,
                    })
                )
            );
            const brands: any[] = brandBatches.flat();

            const indexedByBrandId = brands.reduce<Record<string, Brand>>((acc, brand) => {
                acc[brand.affiliateBrandId] = brand;
                return acc;
            }, {});

            const nextDeals = deals
                .map((deal) => ({
                    ...deal,
                    brand: indexedByBrandId[deal.affiliateId],
                }))
                .filter((deal) => deal.brand);

            setAllDeals(() => nextDeals);
            setLoading(() => false);
        });
    }, [brandLimitConfig, fetchDeals, maxResults]);

    // filter deals as user filters change
    useEffect(() => {
        const mappedDealType = state.dealTypes.filter(isDealTypeLabel).map((dt) => dealTypeMap[dt]);
        const startsWithNumberRegex = /$[0-9]/;

        const nextDeals = allDealsBrandFiltered.filter((deal) => {
            const brand = deal.brand;

            if (!brand) {
                return false;
            }

            if (state.viewBonusOnly && !brand.affiliateBonus) {
                return false;
            }

            if (state.dealTypes.length > 0 && !mappedDealType.includes(deal.dealType)) {
                return false;
            }

            const brandCategories = brand.categories;
            const filteredCategories = state.categories;

            if (
                filteredCategories.length > 0 &&
                !filteredCategories.some((category) => brandCategories.includes(category))
            ) {
                return false;
            }

            if (state.letterFilter === '#') {
                if (!startsWithNumberRegex.test(brand.name)) {
                    return false;
                }
            } else if (
                state.letterFilter !== 'All' &&
                state.letterFilter &&
                !brand.name.toUpperCase().startsWith(state.letterFilter.toUpperCase())
            ) {
                return false;
            }

            return true;
        });

        nextDeals.sort((a: DealUIObjectWithBrand, b: DealUIObjectWithBrand) => {
            switch (state.sortBy.value) {
                case DealsSortBy.EndingSoon:
                    return new Date(a.endAt).getTime() - new Date(b.endAt).getTime();
                case DealsSortBy.Newest:
                    return new Date(b.startAt).getTime() - new Date(a.startAt).getTime();
                case DealsSortBy.MostPopular:
                    return b.sortScore - a.sortScore;
                case DealsSortBy.MaxRebate:
                    return (
                        Math.max(
                            b.brand?.affiliateBonus ?? -Infinity,
                            b.brand?.affiliateRebate ?? -Infinity
                        ) -
                        Math.max(
                            a.brand?.affiliateBonus ?? -Infinity,
                            a.brand?.affiliateRebate ?? -Infinity
                        )
                    );
                case DealsSortBy.NameAsc:
                    return (a.brand?.name ?? '').localeCompare(b.brand?.name ?? '');

                case DealsSortBy.NameDesc:
                    return (b.brand?.name ?? '').localeCompare(a.brand?.name ?? '');
                default:
                    return 0;
            }
        });

        setCurrentDeals(() => nextDeals);
    }, [
        allDealsBrandFiltered,
        state.dealTypes,
        state.categories,
        state.letterFilter,
        state.viewBonusOnly,
        state.sortBy,
    ]);

    const api = useMemo(
        () => ({
            allDeals,
            allDealsBrandFiltered,
            deals: currentDeals,
            loading,
        }),
        [allDeals, allDealsBrandFiltered, currentDeals, loading]
    );

    return api;
}
